/**
 * batchPaint — emit BATCHED `q … Q` operator blocks per resolved plot style,
 * preserving draw order via RUN-LENGTH grouping.
 *
 * Extracted from `exportLayoutPdf` (PDF-4 split) so `sheetRenderer` can share
 * it. Consecutive paths sharing a resolved style key (colour + lineweight +
 * linetype) are painted in a single `q … w RG … (m/l/c)* … S/B/B* … Q` block —
 * NOT one `drawSvgPath` per entity (`PLAN-plot-pdf.md` §3 / Appendix D).
 *
 * Draw order (#177 P2): a GLOBAL bucket-by-style (the prior impl) reorders an
 * A/B/A entity sequence into A,A,B — painting both A's before B, which is the
 * WRONG z-order for overlapping / filled geometry. We instead walk paths in
 * order, accumulate a run while the style key is unchanged, and FLUSH when it
 * changes (or at end). This batches the common case (entities grouped by layer)
 * while painting in exact authored order.
 *
 * PDF-5c (linetype dashes): the run key already folds in the linetype
 * (`plotStyleKey` = colour + lineweight + linetype), so consecutive
 * same-linetype paths are one group. Per group we emit `setDashPattern` for the
 * resolved dash array, scaled to the plotted scale. KNOWN GAP: the typed model
 * has no structured HEADER, so LTSCALE / CELTSCALE / per-entity linetypeScale
 * are NOT available here — they live only in `rawPassthroughSections` (unparsed
 * HEADER). We default LTSCALE=1; a caller may thread real sysvars via
 * `DashEmitOptions.sysvars` once the HEADER is modelled (PDF-6 persistence).
 *
 * PDF-5c.1 (REAL multi-element patterns): the scene `linetypePatterns` registry
 * SUMMARISES every pattern to ONE (dash, gap) pair (the Line2 limitation), so
 * CENTER / DASHDOT / DOT / PHANTOM / BORDER / DIVIDE collapse to a uniform dash.
 * pdf-lib's `setDashPattern` accepts a FULL array, so we render the TRUE acad.lin
 * pattern instead — strictly better fidelity than the screen. `resolveDashesPt`
 * looks up `fullLinetypePattern(name)` (the canonical signed element array,
 * derived from the SAME registry so 2-element DASHED/HIDDEN stay byte-identical),
 * applies the SAME effective scale (LTSCALE × CELTSCALE × MS/PS) × `dashScale`
 * × `MM_TO_PT` to EVERY element, and maps dash→on / gap→off / dot(0)→0-length-on
 * (rendered as a round dot by `LineCap.Round`). Unknown / custom names fall back
 * to the 2-element summary path. Custom (DXF LTYPE-table) dash arrays are still
 * only the 2-element summary — full custom-array support is PDF-6.
 */

import {
  pushGraphicsState,
  popGraphicsState,
  moveTo,
  lineTo,
  appendBezierCurve,
  closePath,
  clipEvenOdd,
  endPath,
  stroke,
  fill,
  setDashPattern,
  setLineCap,
  setLineJoin,
  setLineWidth,
  setFillingRgbColor,
  setStrokingRgbColor,
  LineCapStyle,
  LineJoinStyle,
  PDFName,
  PDFOperator,
  PDFOperatorNames,
  type PDFPage,
} from "pdf-lib";

import { ensureExtGState } from "./extGState";
import { ensureShading } from "./shading";

import {
  plotScalesLineweights,
  type LayoutPlotSettings,
} from "@dxf-ai-editor/drawing-model";

import {
  resolvePlotScale,
  lineweightToPt,
  MM_TO_PT,
  HAIRLINE_PT,
  type PdfTransform,
} from "./pdfTransform";
import {
  plotStyleKey,
  type GradientHatchPrimitive,
  type HatchPatternPrimitive,
  type PathPrimitive,
  type PathSubpath,
  type PlotStyle,
} from "./cadPrimitive";
import {
  linetypeToDashConfig,
  computeEffectiveDash,
  type DashConfig,
  type LinetypeSysvars,
} from "../../scene3d/rendering/linetypePatterns";
import { fullLinetypePattern } from "./linetypePatternsFull";

/**
 * Emit run-length batched `q … Q` operator blocks. Consecutive same-style
 * paths share one block; a style change flushes a new block. Returns the
 * number of blocks emitted.
 *
 * `effectiveScale` (PDF-4 FIX C / Codex #180) is the LAYOUT fit scale actually
 * applied to the geometry (`region.scale` for a fitted Window/Extents/Limits
 * plot; 1 for Layout). When the ScaleLineweights flag is set, the pen width
 * must track the PLOTTED scale, not the settings' nominal `scale`
 * (numerator/denominator, often 1:1 for a fit plot). Callers that fit a region
 * pass `region.scale`; omitting it preserves the prior behaviour
 * (`resolvePlotScale(settings)`).
 *
 * `dashOptions` (PDF-5c) controls linetype dash emission. A non-Continuous run
 * emits `setDashPattern(dashesPt, 0)` before its stroke; a Continuous run emits
 * `setDashPattern([], 0)` (solid). Dash lengths are in drawing units (mm) and
 * are scaled to the PLOTTED scale like the geometry — `dashScale` (the same
 * `region.scale` fit threaded for lineweights) — then through LTSCALE /
 * CELTSCALE via `computeEffectiveDash`, then `× MM_TO_PT`. Omitting it leaves
 * every run solid (prior PDF-1..4 behaviour, byte-identical).
 */
export interface DashEmitOptions {
  /** Plotted fit scale applied to dash lengths (region.scale; defaults 1). */
  dashScale?: number;
  /** LTSCALE / CELTSCALE / MS-PS sysvars (defaults LTSCALE=1 — solid baseline). */
  sysvars?: LinetypeSysvars;
  /** Model vs paper space (selects MSLTSCALE / PSLTSCALE). Defaults "paper". */
  viewportKind?: "model" | "paper" | "viewport";
  /**
   * PDF-6.4 — AutoCAD "Lines merge / Overwrite" PC3 plot setting.
   *
   *   - `"overwrite"` (the default and pre-6.4 behaviour): NO `/BM` ExtGState
   *     emitted, PDF Normal blend mode (top objects hide bottom). Output is
   *     byte-identical to PDF-6.3b for the legacy default.
   *   - `"merge"`: every painted run is wrapped in an ExtGState carrying
   *     `/BM /Multiply` so overlapping geometry darkens like AutoCAD's "Lines
   *     merge" plot setting. The gs op is emitted EVEN for fully opaque runs
   *     (the blend mode itself is the override). In merge mode each primitive
   *     in a run is painted as its OWN paint op (Fix C, Codex #207) so
   *     coincident same-style geometry MULTIPLIES against itself under the
   *     run's `/BM /Multiply` — a single coalesced paint would composite the
   *     whole run as one source and never multiply self-overlaps. Overwrite
   *     mode keeps the coalesced single-paint-per-run path (perf).
   *
   * Bundled into the existing `dashOptions` bag to avoid widening the function
   * signature (sheetRenderer already builds and threads this struct).
   */
  mergeControl?: "overwrite" | "merge";
}

export function emitBatchedPaths(
  page: PDFPage,
  paths: PathPrimitive[],
  transform: PdfTransform,
  settings: LayoutPlotSettings,
  effectiveScale?: number,
  dashOptions?: DashEmitOptions,
): number {
  // PDF-5c dash scaling inputs. `dashScale` mirrors `effectiveScale`
  // (region.scale) so dash lengths track the plotted scale like geometry; the
  // sysvars default to LTSCALE=1 (no structured HEADER in the model — see
  // module note). Resolved once per call.
  const dashScale =
    dashOptions?.dashScale !== undefined &&
    Number.isFinite(dashOptions.dashScale) &&
    dashOptions.dashScale > 0
      ? dashOptions.dashScale
      : 1;
  const sysvars: LinetypeSysvars = dashOptions?.sysvars ?? {
    LTSCALE: 1,
    MSLTSCALE: 1,
    PSLTSCALE: 1,
  };
  const viewportKind = dashOptions?.viewportKind ?? "paper";
  // PDF-6.4: Lines merge / Overwrite. "merge" wraps every run in `/BM
  // /Multiply` so overlapping geometry darkens (PC3 parity); "overwrite" (the
  // default and absent fallback) emits no `/BM` and preserves PDF Normal blend
  // mode — byte-identical to PDF-6.3b output.
  const mergeControl: "overwrite" | "merge" =
    dashOptions?.mergeControl ?? "overwrite";
  const mergeBlendMode: "Multiply" | undefined =
    mergeControl === "merge" ? "Multiply" : undefined;

  /**
   * Resolve a plot style's linetype → a PDF dash array in PT (empty = solid).
   *
   * PDF-5c.1: for a STANDARD linetype we emit the FULL multi-element acad.lin
   * pattern (CENTER / DASHDOT / DOT / PHANTOM / BORDER / DIVIDE keep their real
   * short-dashes + dots — better fidelity than the on-screen Line2 summary).
   * For an unknown / custom name we fall back to the 2-element summary
   * (`linetypeToDashConfig` → `computeEffectiveDash`) the editor uses.
   *
   * Scaling is IDENTICAL on both paths: the effective scale `S` =
   * LTSCALE × CELTSCALE × MS/PS (exactly what `computeEffectiveDash` applies)
   * × `dashScale` (region fit) × `MM_TO_PT`, applied to EVERY element — so a
   * 2-element linetype (DASHED/HIDDEN) is byte-identical whether it takes the
   * full-pattern or the summary path.
   */
  const resolveDashesPt = (style: PlotStyle): number[] => {
    const base: DashConfig = linetypeToDashConfig(style.linetype);
    // CONTINUOUS / sentinel / unknown-summary → no dashes (solid). A name that
    // summarises to solid never has a full pattern either, so this also covers
    // the full-pattern path's solid cases.
    if (base.dashSize === 0 && base.gapSize === 0) return [];

    // PDF-6.1: per-entity linetype scale (DXF group 48 → CELTSCALE). The
    // doc-level sysvars carry LTSCALE/MS/PS; the per-style scale is the
    // entity's own CELTSCALE multiplier, applied on top via the sysvars
    // CELTSCALE field. A style without an explicit scale (undefined → 1)
    // preserves the prior behaviour.
    const styleSysvars: LinetypeSysvars =
      style.linetypeScale !== undefined && style.linetypeScale !== sysvars.CELTSCALE
        ? { ...sysvars, CELTSCALE: style.linetypeScale }
        : sysvars;

    // The effective scale `computeEffectiveDash` applies to a (dash, gap) pair,
    // recovered by running it on a UNIT base — `{1,1}` → `{S, S}` — so we get
    // the exact same factor (incl. the PSLTSCALE=0 edge case) and apply it to
    // every full-pattern element. `× dashScale × MM_TO_PT` finishes the convert.
    const unit = computeEffectiveDash(
      { dashSize: 1, gapSize: 1, dashOffset: 0 },
      style.lineweightMm,
      viewportKind,
      styleSysvars,
    );
    const ptPerDrawingUnit = unit.dashSize * dashScale * MM_TO_PT;
    if (!(ptPerDrawingUnit > 0)) return [];

    // PDF-5c.1: STANDARD linetype → FULL multi-element pattern (signed drawing
    // units: + dash, − gap, 0 dot). Map to a PDF `[on,off,on,off,…]` array.
    const full = fullLinetypePattern(style.linetype);
    if (full) {
      const pdfArr = toPdfDashArray(full, ptPerDrawingUnit);
      // A degenerate result (no on/off content) → solid rather than an invalid
      // empty-but-dashed array.
      return pdfArr.length >= 2 ? pdfArr : [];
    }

    // Fallback: the 2-element summary path (unknown / custom linetype that the
    // registry still summarises to a dash/gap pair). Byte-identical to PDF-5c.
    const eff = computeEffectiveDash(
      base,
      style.lineweightMm,
      viewportKind,
      styleSysvars,
    );
    const dashPt = eff.dashSize * dashScale * MM_TO_PT;
    const gapPt = eff.gapSize * dashScale * MM_TO_PT;
    if (!(dashPt > 0) || !(gapPt > 0)) return [];
    return [dashPt, gapPt];
  };
  // PDF-4 FIX C: when lineweights scale with the plot, use the effective fit
  // scale (region.scale) rather than the settings-derived scale. Falls back to
  // resolvePlotScale(settings) when no override is supplied (prior behaviour).
  const lineweightScale =
    plotScalesLineweights(settings) &&
    effectiveScale !== undefined &&
    Number.isFinite(effectiveScale)
      ? effectiveScale
      : resolvePlotScale(settings);
  // Convert a resolved lineweight (mm) → pt honouring the ScaleLineweights flag
  // with the EFFECTIVE scale. Mirrors `lineweightToPt` but threads the fit
  // scale so a fitted plot's pens track the plotted scale (Codex #180 FIX C).
  const resolveLwPt = (lineweightMm: number): number => {
    const scaledMm = plotScalesLineweights(settings)
      ? lineweightMm * lineweightScale
      : lineweightMm;
    const pt = scaledMm * MM_TO_PT;
    if (!Number.isFinite(pt) || pt <= 0) return HAIRLINE_PT;
    return Math.max(pt, HAIRLINE_PT);
  };
  // Combined fill+stroke paint ops (B* = fill-even-odd-and-stroke, B =
  // fill-nonzero-and-stroke). PDF-1 strokes by default; closed loops that opt
  // into a fill (and still stroke) use these so the path is painted once.
  const fillStrokeEvenOdd = PDFOperator.of(
    PDFOperatorNames.FillEvenOddAndStroke,
  );
  const fillStrokeNonZero = PDFOperator.of(
    PDFOperatorNames.FillNonZeroAndStroke,
  );
  // FILL-ONLY even-odd (`f*`). pdf-lib has no standalone helper for it (only
  // `fill()` = `f`), so we build the operator directly. A SOLID hatch fills its
  // region without stroking the boundary (PDF-5a P1).
  const fillEvenOddOnly = PDFOperator.of(PDFOperatorNames.FillEvenOdd);

  let groupCount = 0;
  // Current run being accumulated. `key` is the run key of the run; `null`
  // means no run is open yet. The run key combines the plot style AND the
  // paint mode so a FILL-ONLY hatch (`f*`) and a STROKE line (`S`) — which
  // share a colour/lineweight — never merge into one paint op (PDF-5a): they
  // need different terminal operators.
  let runKey: string | null = null;
  let runStyle: PlotStyle | null = null;
  let runMode: PaintMode | null = null;
  let runItems: PathPrimitive[] = [];

  /** Flush the accumulated run as one `q … Q` block. */
  const flushRun = (): void => {
    if (runStyle === null || runMode === null || runItems.length === 0) return;
    const style = runStyle;
    const mode = runMode;
    // FIX C (#180): use the effective fit scale for the pen, not settings.scale.
    const lwPt = resolveLwPt(style.lineweightMm);
    const ops: PDFOperator[] = [pushGraphicsState()];
    // PDF-6.3b: when the run carries an alpha from CTB/STB screening
    // (`screen ∈ (0, 100)`) OR PDF-6.4 per-entity transparency (DXF 440), load
    // a page-Resources ExtGState dict carrying `/CA` (stroke) + `/ca` (fill)
    // alpha. PDF-6.4: when the user picked "Merge" (Lines merge), additionally
    // emit `/BM /Multiply` so overlapping geometry darkens — and emit the gs
    // op EVEN for fully opaque runs (the blend mode itself is the override).
    // `ensureExtGState` dedups by `(alpha, blendMode)`, so distinct combos
    // register distinct dicts but a repeated combo reuses the same one.
    // alpha ∈ [0, 1): a fully-transparent entity (alpha === 0, DXF 440
    // `0x02000000`) MUST still register an ExtGState (`/CA 0 /ca 0`) so it
    // plots invisible — AutoCAD-parity for 100% transparency. Excluding 0
    // (the old `> 0` guard) let a transparent entity fall through to an opaque
    // paint. alpha === 1 (opaque) needs no dict.
    const needsAlpha =
      style.alpha !== undefined && style.alpha >= 0 && style.alpha < 1;
    const needsBlend = mergeBlendMode !== undefined;
    if (needsAlpha || needsBlend) {
      const gsName = ensureExtGState(page, {
        alpha: needsAlpha ? style.alpha : undefined,
        blendMode: mergeBlendMode,
      });
      ops.push(
        PDFOperator.of(PDFOperatorNames.SetGraphicsStateParams, [
          PDFName.of(gsName),
        ]),
      );
    }
    // Stroke colour + width only matter when the run actually strokes.
    if (mode !== "fill") {
      ops.push(setLineWidth(lwPt));
      ops.push(
        setStrokingRgbColor(style.color.r, style.color.g, style.color.b),
      );
      // PDF-5c: round caps + joins for clean dashed corners (AutoCAD-ish).
      // Set once per stroked group (cheap; uniform within the run's style).
      ops.push(setLineCap(LineCapStyle.Round));
      ops.push(setLineJoin(LineJoinStyle.Round));
      // PDF-5c: dash pattern for the run's linetype. Continuous → `[] 0 d`
      // (solid); a dashed/center/hidden run → its scaled dash array. Set once
      // per group since the linetype is folded into the run key.
      const dashesPt = resolveDashesPt(style);
      ops.push(setDashPattern(dashesPt, 0));
    }
    // Fill colour only matters when the run fills.
    if (mode === "fill" || mode === "fillStroke") {
      ops.push(setFillingRgbColor(style.color.r, style.color.g, style.color.b));
    }

    // The terminal paint op for this run's mode, given a fill-rule (even-odd vs
    // nonzero). The fill rule is uniform within a run for the COALESCED path
    // (folded into the run key), but in per-primitive merge mode we recompute it
    // per primitive so a mixed run still paints each primitive correctly.
    const paintOpFor = (evenOdd: boolean): PDFOperator => {
      switch (mode) {
        case "fill":
          return evenOdd ? fillEvenOddOnly : fill();
        case "fillStroke":
          return evenOdd ? fillStrokeEvenOdd : fillStrokeNonZero;
        case "stroke":
        default:
          return stroke();
      }
    };

    if (mergeBlendMode !== undefined) {
      // PDF-6.4 (Fix C — Codex #207): in MERGE mode each primitive is painted
      // as its OWN paint op so coincident same-style geometry MULTIPLIES against
      // itself under the run's `/BM /Multiply` (set once above and persisting
      // across the run's `q…Q` scope) — AutoCAD "Lines merge" darkens overlapping
      // edges of the same layer. A single coalesced paint op would composite the
      // whole run as ONE source and never multiply self-overlaps. The fill rule
      // is computed PER primitive (each path carries its own `fill`).
      for (const p of runItems) {
        emitPathGeometry(ops, p, transform);
        ops.push(paintOpFor(p.fill === "evenodd"));
      }
    } else {
      // OVERWRITE mode (default): coalesce the whole run into ONE paint op (perf
      // — Normal blend hides lower geometry, so self-overlap multiply is moot).
      // Even-odd vs nonzero is uniform within a run (folded into the run key).
      let evenOdd = false;
      for (const p of runItems) {
        emitPathGeometry(ops, p, transform);
        if (p.fill === "evenodd") evenOdd = true;
      }
      ops.push(paintOpFor(evenOdd));
    }
    ops.push(popGraphicsState());

    // Chunk the variadic push: `pushOperators(...ops)` spreads the array as
    // call arguments, which overflows the JS call stack for large groups
    // (100k+ entities → RangeError). pdf-lib appends operators to the same page
    // content stream across calls, so chunked pushes preserve operator order.
    flushChunkedOps(page, ops);

    groupCount += 1;
    runItems = [];
  };

  for (const p of paths) {
    const mode = paintMode(p);
    // Run key = style + paint mode + fill rule. A change in any flushes the
    // run, preserving authored draw order (A/B/A → A, B, A — three blocks).
    const key = `${plotStyleKey(p.style)}|${mode}|${p.fill}`;
    if (key !== runKey) {
      flushRun();
      runKey = key;
      runStyle = p.style;
      runMode = mode;
    }
    runItems.push(p);
  }
  flushRun();

  return groupCount;
}

/**
 * "On" length (pt) used for a DOT element (linetype element value 0).
 *
 * DOT RENDERING TECHNIQUE (PDF-5c.1): a 0-length ON segment + `LineCap.Round`
 * (set once per stroked group in PDF-5c, batchPaint line ~226) renders as a
 * round dot — the SAME technique the SVG sibling uses
 * (`linetypeToSvgDashArray` emits `"0"` for dots and relies on
 * `stroke-linecap: round`). We use a LITERAL `0` (not an epsilon) so the PDF
 * dot is identical to the on-screen / SVG dot and there is no divergence
 * between export paths. pdf-lib's `setDashPattern` accepts a `0` element.
 *
 * FALLBACK (documented, not currently used): if a target PDF rasteriser failed
 * to paint a round cap on a literal-0 on-segment, switch this to a tiny
 * positive epsilon (e.g. `0.01` pt ≈ 0.0035 mm, far below visual resolution).
 * The unit tests accept either `0` or a sub-0.1 pt epsilon for the dot slot. */
const DOT_ON_PT = 0;

/**
 * Convert a FULL signed linetype element array (drawing units: + dash, − gap,
 * 0 dot) into a pdf-lib dash array in PT.
 *
 * Each element is multiplied by `ptPerUnit` (= effective LTSCALE/CELTSCALE/MS-PS
 * scale × region dashScale × MM_TO_PT) and classified as ON (dash/dot, length
 * ≥ 0) or OFF (gap, length < 0). The result is `[on, off, on, off, …]`:
 *   - dash (positive) → its scaled length as an ON segment;
 *   - gap  (negative) → |scaled length| as an OFF segment;
 *   - dot  (zero)     → a {@link DOT_ON_PT} ON segment (round-cap dot).
 *
 * Normalisation (acad.lin patterns already satisfy this, so it is a no-op for
 * them and preserves byte-identity): adjacent same-type elements are merged
 * (summed), the array is forced to BEGIN with an ON segment (a leading gap is
 * folded as the dash `phase` would be — but we keep phase 0 and instead prepend
 * a 0-length on so the count stays even and the first painted mark is on), and
 * the length is made EVEN by appending a 0-length OFF if needed. This keeps the
 * visual pattern intact for any well-formed or slightly-irregular input.
 */
export function toPdfDashArray(
  elements: readonly number[],
  ptPerUnit: number,
): number[] {
  // Classify + scale each element into (isOn, lengthPt) pairs, dropping nothing.
  type Seg = { on: boolean; len: number };
  const segs: Seg[] = [];
  for (const el of elements) {
    if (el === 0) {
      // DOT — a zero-length pen-down mark → epsilon ON segment.
      segs.push({ on: true, len: DOT_ON_PT });
    } else if (el > 0) {
      segs.push({ on: true, len: el * ptPerUnit });
    } else {
      segs.push({ on: false, len: Math.abs(el) * ptPerUnit });
    }
  }
  if (segs.length === 0) return [];

  // Merge adjacent same-type segments (sum their lengths). For canonical
  // acad.lin patterns (strict on/off alternation) this changes nothing.
  const merged: Seg[] = [];
  for (const s of segs) {
    const last = merged[merged.length - 1];
    if (last && last.on === s.on) {
      last.len += s.len;
    } else {
      merged.push({ ...s });
    }
  }

  // Force the array to BEGIN with an ON segment. A leading OFF (gap-first
  // pattern) would otherwise paint a leading gap as if it were an on-length;
  // prepend a 0-length ON so the first real element stays OFF and the on/off
  // phase is correct. (No acad.lin standard pattern is gap-first, so this is a
  // no-op for them.)
  if (!merged[0]!.on) {
    merged.unshift({ on: true, len: 0 });
  }

  // Flatten ON/OFF alternation into the `[on, off, on, off, …]` PDF array.
  const out: number[] = merged.map((s) => s.len);

  // Even length: pdf-lib requires an even count (on/off pairs). A pattern that
  // ends on an ON segment (no trailing gap) needs a trailing 0-length OFF.
  if (out.length % 2 !== 0) out.push(0);

  return out;
}

/** How a path is painted (its terminal PDF operator). */
type PaintMode = "stroke" | "fill" | "fillStroke";

/**
 * Resolve a path's paint mode:
 *   - `stroke === false` + a fill rule → FILL ONLY (`f` / `f*`) — AutoCAD solid
 *     hatch fills its region without stroking the boundary (PDF-5a P1);
 *   - a fill rule with stroking → FILL + STROKE (`B` / `B*`) — PDF-1 closed
 *     loop that opts into a fill;
 *   - otherwise STROKE (`S`).
 */
function paintMode(p: PathPrimitive): PaintMode {
  if (p.fill !== "none") {
    return p.stroke === false ? "fill" : "fillStroke";
  }
  return "stroke";
}

/**
 * Push the `m / l / c (… closePath)` operators for one path primitive,
 * including any extra closed loops carried in `subpaths` (PDF-5a multi-loop
 * hatch). Each subpath is its own `moveTo … closePath` block so a SINGLE fill
 * op spanning all subpaths produces even-odd holes.
 */
function emitPathGeometry(
  ops: PDFOperator[],
  p: PathPrimitive,
  transform: PdfTransform,
): void {
  emitSubpath(ops, { start: p.start, segments: p.segments }, p.closed, transform);
  if (p.subpaths) {
    for (const sub of p.subpaths) {
      emitSubpath(ops, sub, true, transform);
    }
  }
}

/** Push one subpath's `moveTo … (closePath)` operators. */
function emitSubpath(
  ops: PDFOperator[],
  sub: PathSubpath,
  closed: boolean,
  transform: PdfTransform,
): void {
  const s = transform(sub.start.x, sub.start.y);
  ops.push(moveTo(s[0], s[1]));
  for (const seg of sub.segments) {
    if (seg.kind === "line") {
      const to = transform(seg.to.x, seg.to.y);
      ops.push(lineTo(to[0], to[1]));
    } else {
      const c1 = transform(seg.c1.x, seg.c1.y);
      const c2 = transform(seg.c2.x, seg.c2.y);
      const to = transform(seg.to.x, seg.to.y);
      ops.push(appendBezierCurve(c1[0], c1[1], c2[0], c2[1], to[0], to[1]));
    }
  }
  if (closed) ops.push(closePath());
}

/**
 * Emit ONE pattern-fill hatch (PDF-5a P2) as a self-contained `q … Q` block:
 *
 *   q
 *   (loop1 moveTo/segments/closePath) (loop2 …) …   ← boundary loops
 *   W* n                                            ← even-odd CLIP, no paint
 *   <stroke colour + hairline width>
 *   (pattern line moveTo/lineTo + optional dashes)* ← clipped to the region
 *   S
 *   Q
 *
 * PDF clips the (effectively infinite) pattern lines to the boundary loops, so
 * we never compute line/boundary intersections (mirrors the VP clip in
 * `sheetRenderer`). Returns 1 (one block emitted) for accounting, or 0 if the
 * hatch had no loops / no lines.
 */
/**
 * Push a (possibly large) operator list to the page in chunks. `pushOperators`
 * spreads the array as call arguments, which overflows the JS call stack for
 * large groups (100k+ entities → RangeError). pdf-lib appends operators to the
 * same content stream across calls, so chunked pushes preserve operator order.
 * Shared by every emitter (paths / pattern hatch / gradient hatch).
 */
function flushChunkedOps(page: PDFPage, ops: PDFOperator[]): void {
  const PUSH_CHUNK = 8000;
  for (let i = 0; i < ops.length; i += PUSH_CHUNK) {
    page.pushOperators(...ops.slice(i, i + PUSH_CHUNK));
  }
}

/**
 * Emit the shared hatch-fill PREAMBLE used by BOTH the pattern hatch
 * ({@link emitHatchPattern}) and the gradient hatch ({@link emitGradientHatch}):
 *   pushGraphicsState → CTB/STB-alpha + Lines-merge ExtGState wrap (PDF-6.3b /
 *   6.4) → even-odd boundary clip (`W* n`). alpha ∈ [0, 1): a fully-transparent
 *   hatch (alpha === 0) still registers `/CA 0 /ca 0` so it plots invisible.
 * Returns the ops array; the caller appends its own paint (pattern strokes vs
 * the `sh` shading) then `popGraphicsState()` + the chunked `pushOperators`.
 */
function pushHatchFillPreamble(
  page: PDFPage,
  style: HatchPatternPrimitive["style"],
  loops: HatchPatternPrimitive["loops"],
  transform: PdfTransform,
  hatchOptions: { mergeControl?: "overwrite" | "merge" } | undefined,
): PDFOperator[] {
  const ops: PDFOperator[] = [pushGraphicsState()];
  const alpha = style.alpha;
  const needsAlpha = alpha !== undefined && alpha >= 0 && alpha < 1;
  const mergeBlendMode: "Multiply" | undefined =
    hatchOptions?.mergeControl === "merge" ? "Multiply" : undefined;
  if (needsAlpha || mergeBlendMode !== undefined) {
    const gsName = ensureExtGState(page, {
      alpha: needsAlpha ? alpha : undefined,
      blendMode: mergeBlendMode,
    });
    ops.push(
      PDFOperator.of(PDFOperatorNames.SetGraphicsStateParams, [
        PDFName.of(gsName),
      ]),
    );
  }
  // Boundary loops → even-odd clip (so multi-loop holes clip correctly too).
  for (const loop of loops) {
    emitSubpath(ops, loop, true, transform);
  }
  ops.push(clipEvenOdd());
  ops.push(endPath());
  return ops;
}

export function emitHatchPattern(
  page: PDFPage,
  hatch: HatchPatternPrimitive,
  transform: PdfTransform,
  settings: LayoutPlotSettings,
  hatchOptions?: { mergeControl?: "overwrite" | "merge" },
): number {
  if (hatch.loops.length === 0 || hatch.lines.length === 0) return 0;

  const lwPt = lineweightToPt(hatch.style.lineweightMm, settings);
  const ops = pushHatchFillPreamble(
    page,
    hatch.style,
    hatch.loops,
    transform,
    hatchOptions,
  );

  // Pattern strokes — hairline-ish at the resolved hatch lineweight.
  ops.push(setLineWidth(Math.max(lwPt, HAIRLINE_PT)));
  ops.push(
    setStrokingRgbColor(hatch.color.r, hatch.color.g, hatch.color.b),
  );

  // Group lines by their dash signature so we set the dash pattern once per
  // distinct dash run (most patterns are solid → one group; dashed families
  // share their own group). A single setDashPattern precedes each group.
  let lastDashKey: string | null = null;
  for (const line of hatch.lines) {
    const dashKey = line.dashes.length === 0 ? "" : line.dashes.join(",");
    if (dashKey !== lastDashKey) {
      if (line.dashes.length === 0) {
        ops.push(setDashPattern([], 0));
      } else {
        // pdf-lib setDashPattern expects positive dash/gap lengths in pt.
        const dashesPt = line.dashes.map((d) => Math.abs(d) * MM_TO_PT);
        ops.push(setDashPattern(dashesPt, 0));
      }
      lastDashKey = dashKey;
    }
    const from = transform(line.from.x, line.from.y);
    const to = transform(line.to.x, line.to.y);
    ops.push(moveTo(from[0], from[1]));
    ops.push(lineTo(to[0], to[1]));
  }
  ops.push(stroke());
  ops.push(popGraphicsState());

  flushChunkedOps(page, ops);
  return 1;
}

/**
 * Compute an axial gradient's axis endpoints in pt (PDF-6.5b).
 *
 * The axis runs through the bbox centre along the `rotation` unit direction.
 * Its half-span is the projection of the bbox corners onto that direction
 * (so the axis spans the full extent of the boundary in the gradient
 * direction). `shift` ∈ [0, 1] is AutoCAD's "centered" offset: shift = 0 leaves
 * the axis centred; a non-zero shift slides the axis MIDPOINT along the
 * direction by `shift × halfSpan` (AutoCAD biases the colour transition toward
 * one end). Both endpoints are then transformed to pt so the page / VP rotation
 * + scale carry correctly.
 *
 * Returns `[x0, y0, x1, y1]` in pt.
 */
function axialCoordsPt(
  bbox: GradientHatchPrimitive["bbox"],
  rotation: number,
  shift: number,
  transform: PdfTransform,
): number[] {
  const cx = (bbox.minX + bbox.maxX) / 2;
  const cy = (bbox.minY + bbox.maxY) / 2;
  const dirX = Math.cos(rotation);
  const dirY = Math.sin(rotation);

  // Project all four corners onto the direction to find the signed half-span.
  const corners: Array<[number, number]> = [
    [bbox.minX, bbox.minY],
    [bbox.maxX, bbox.minY],
    [bbox.maxX, bbox.maxY],
    [bbox.minX, bbox.maxY],
  ];
  let minProj = Infinity;
  let maxProj = -Infinity;
  for (const [px, py] of corners) {
    const proj = (px - cx) * dirX + (py - cy) * dirY;
    if (proj < minProj) minProj = proj;
    if (proj > maxProj) maxProj = proj;
  }
  const halfSpan = (maxProj - minProj) / 2;
  // AutoCAD `shift` biases the axis midpoint along the direction (centred at 0).
  const s = Math.max(-1, Math.min(1, shift)) * halfSpan;

  const startX = cx + dirX * (s - halfSpan);
  const startY = cy + dirY * (s - halfSpan);
  const endX = cx + dirX * (s + halfSpan);
  const endY = cy + dirY * (s + halfSpan);

  const p0 = transform(startX, startY);
  const p1 = transform(endX, endY);
  return [p0[0], p0[1], p1[0], p1[1]];
}

/**
 * Compute a radial gradient's disc coords in pt (PDF-6.5b).
 *
 * center = transform(bbox centre); r0 = 0; r1 = half the TRANSFORMED bbox
 * diagonal (we transform two diagonal corners and halve their pt distance so
 * the radius absorbs the page / VP scale exactly like the geometry). Returns
 * `[cx, cy, 0, cx, cy, r1]` in pt.
 */
function radialCoordsPt(
  bbox: GradientHatchPrimitive["bbox"],
  transform: PdfTransform,
): number[] {
  const cxW = (bbox.minX + bbox.maxX) / 2;
  const cyW = (bbox.minY + bbox.maxY) / 2;
  const center = transform(cxW, cyW);

  // Transform two diagonal corners; half their pt distance is the outer radius.
  const a = transform(bbox.minX, bbox.minY);
  const b = transform(bbox.maxX, bbox.maxY);
  const r1 = Math.hypot(b[0] - a[0], b[1] - a[1]) / 2;

  return [center[0], center[1], 0, center[0], center[1], r1];
}

/**
 * Emit ONE gradient-fill hatch (PDF-6.5b) as a self-contained `q … Q` block:
 *
 *   q
 *   [alpha/blend gs]                                ← same wrap as emitHatchPattern
 *   (loop1 moveTo/segments/closePath) (loop2 …) …   ← boundary loops
 *   W* n                                            ← even-odd CLIP, no paint
 *   sh /ShN                                         ← paint the registered shading
 *   Q
 *
 * The shading (axial ShadingType 2 / radial ShadingType 3) is registered on the
 * page via {@link ensureShading} with the axis/disc coords computed in pt from
 * the boundary bbox (so VP rotation/scale carry correctly). `/Extend [true
 * true]` on the shading makes the gradient cover the whole clipped region.
 * Returns 1 (one block emitted) for accounting, or 0 if the hatch had no loops.
 */
export function emitGradientHatch(
  page: PDFPage,
  prim: GradientHatchPrimitive,
  transform: PdfTransform,
  settings: LayoutPlotSettings,
  hatchOptions?: { mergeControl?: "overwrite" | "merge" },
): number {
  void settings; // gradient fill carries no lineweight; kept for call-site parity
  if (prim.loops.length === 0) return 0;

  // Same q → CTB/STB-alpha + Lines-merge ExtGState → even-odd clip preamble as
  // the pattern hatch (shared helper); we then paint a shading instead of strokes.
  const ops = pushHatchFillPreamble(
    page,
    prim.style,
    prim.loops,
    transform,
    hatchOptions,
  );

  // Axis / disc geometry in pt (transformed so VP rotation + scale carry).
  const g = prim.gradient;
  const coords =
    g.shadingType === "axial"
      ? axialCoordsPt(prim.bbox, g.rotation, g.shift, transform)
      : radialCoordsPt(prim.bbox, transform);

  const shName = ensureShading(page, {
    shadingType: g.shadingType,
    coords,
    color0: g.color0,
    color1: g.color1,
    symmetric: g.symmetric,
    inverse: g.inverse,
    curved: g.name.trim().toUpperCase().includes("CURVED"),
  });

  // Paint the shading across the clipped region (`sh` operator).
  ops.push(PDFOperator.of(PDFOperatorNames.ShadingFill, [PDFName.of(shName)]));
  ops.push(popGraphicsState());

  flushChunkedOps(page, ops);
  return 1;
}
